
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Plus, PercentIcon, MoreHorizontal, Edit, Trash } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { feesApportionPartyApi } from "@/services/api";
import { FeesApportionParty } from "@/models";

const FeesApportionParties = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [parties, setParties] = useState<FeesApportionParty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  
  // Fetch fees apportion parties
  useEffect(() => {
    const fetchParties = async () => {
      try {
        setIsLoading(true);
        const data = await feesApportionPartyApi.list();
        setParties(data || []);
      } catch (err: any) {
        setError(err);
        toast({
          title: "Error loading fees apportion parties",
          description: err.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchParties();
  }, [toast]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter parties by search term
  const filteredParties = searchTerm 
    ? parties.filter(party => 
        party.entity_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (party.description && party.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : parties;

  if (error) {
    console.error("Error fetching fees apportion parties:", error);
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fees Apportion Parties</h1>
            <p className="text-gray-500">
              Manage entities that receive a portion of fees
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild>
              <Link to="/backoffice/fees-apportion-parties/new">
                <Plus className="h-4 w-4 mr-2" />
                New Party
              </Link>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Fees Apportion Parties</CardTitle>
            <CardDescription>
              Showing {filteredParties.length} parties
            </CardDescription>
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search parties..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              </div>
            ) : filteredParties.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Party Name</TableHead>
                      <TableHead className="hidden md:table-cell">Description</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredParties.map((party: FeesApportionParty) => (
                      <TableRow key={party.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center">
                            <PercentIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                            <Link 
                              to={`/backoffice/fees-apportion-parties/${party.id}`}
                              className="hover:underline"
                            >
                              {party.entity_name}
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {party.description || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/backoffice/fees-apportion-parties/${party.id}`}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/backoffice/fees-apportion-parties/${party.id}`} className="text-red-600">
                                  <Trash className="mr-2 h-4 w-4" />
                                  Delete
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-10">
                <PercentIcon className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No fees apportion parties found</h3>
                <p className="text-sm text-muted-foreground mt-2 mb-6">
                  {searchTerm
                    ? "Try a different search term"
                    : "Get started by creating your first fees apportion party"}
                </p>
                <Button asChild>
                  <Link to="/backoffice/fees-apportion-parties/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Fees Apportion Party
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default FeesApportionParties;
